import {
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  MailOutlined as ContactMailIcon,
  PhoneOutlined as ContactPhoneIcon,
  UserOutlined,
} from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Descriptions,
  Form,
  Input,
  Popconfirm,
  Popover,
  Row,
  Select,
  Space,
  Tag,
  Tooltip,
  message,
} from "antd";
import { DATE_TIME_FORMAT } from "common/constants";
import { formatError } from "common/redux/middleware/queryErrorLogger";
import { camelToWords, snakeToCamelCase, toTitleCase } from "common/utils";
import { DisplayComponent, DisplayPhoneNumber } from "components/formItems";
import { useUserType } from "features/auth";
import {
  useArchiveDebtorContactsMutation,
  useCreateDebtorContactsMutation,
  useReplicatePacketedDebtorContactsMutation,
  useUpdateDebtorContactsMutation,
} from "features/debtorSider/agencyPortal/debtorSiderAPI";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import { PERMISSIONS } from "features/permissions";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import moment from "moment-timezone";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { useCompactMode } from "providers/compactModeProvider";
import styled from "styled-components";

const StyledCard = styled.div`
  margin-top: 0px;
  margin-left: 8px;
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;

  h4 {
    margin: 2px 0;
  }
`;

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StyledTag = styled(Tag)`
  margin-right: 0;
  font-weight: normal;
`;

const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 4px;
  }
`;

const StyledContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledValue = styled.span`
  flex: 1;
  overflow-wrap: anywhere;
`;

const StyledLabel = styled.span`
  text-transform: capitalize;
  color: grey;
  font-size: 12px;
  flex: 1;
`;

const StyledInfoCircleOutlined = styled(InfoCircleOutlined)`
  color: #1677ff;
  margin-left: 4px;
`;

const StyledCompactRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 0;
  min-width: 0;

  .ant-btn-link {
    padding: 0;
    min-width: auto;
    height: auto;
    line-height: 1;
  }
`;

const StyledCompactPrimary = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledTrailingActions = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const StyledLeadingIcon = styled(UserOutlined)`
  color: #8c8c8c;
  flex-shrink: 0;
`;

const StyledInfoIcon = styled(InfoCircleOutlined)`
  color: #1677ff;
  cursor: pointer;
`;

const StyledDescriptions = styled(Descriptions)`
  white-space: nowrap;
`;

const StyledContactMailIcon = styled(ContactMailIcon)`
  color: #52c41a;
  font-size: 12px;
  flex-shrink: 0;
`;

const StyledContactPhoneIcon = styled(ContactPhoneIcon)`
  color: #52c41a;
  font-size: 12px;
  flex-shrink: 0;
`;

export function DebtorContactInfoCard({
  triggerReplicatePacketedDebtorUpdatesModal = () => {},
  debtorContact = undefined,
  isNew = undefined,
  onCreate = undefined,
  onCancel = undefined,
  title = undefined,
  profileType = "consumer",
  isFirstItem = true,
}) {
  const { isAgencyUserType } = useUserType();
  const { isCompactMode } = useCompactMode();
  const [form] = Form.useForm();
  const { debtorId } = useParams();
  const [isEditing, setIsEditing] = useState(isNew || false);
  const { data: constants } = useFetchBackendConstantsQuery();
  const [updateDebtorContact, { isLoading: isUpdateLoading }] = useUpdateDebtorContactsMutation();
  const [createDebtorContact, { isLoading: isCreateLoading }] = useCreateDebtorContactsMutation();
  const [archiveDebtorContact, { isLoading: isDeleteLoading }] = useArchiveDebtorContactsMutation();
  const {
    data: {
      [PERMISSIONS.DEBTOR_CONTACT__CREATE]: isAddDebtorContactInfoAuthorized,
      [PERMISSIONS.DEBTOR_CONTACT__UPDATE]: isUpdateDebtorContactInfoAuthorized,
      [PERMISSIONS.DEBTOR_CONTACT__DELETE]: isDeleteDebtorContactInfoAuthorized,
    },
  } = useAuthorizations();
  const [replicatePacketedDebtorContacts] = useReplicatePacketedDebtorContactsMutation();
  const editableDataSourceTypeValues = constants?.editableDataSourceTypes?.map(
    ({ value }) => value,
  );

  const archiveContact = async () => {
    const result = await archiveDebtorContact({
      debtorId,
      contactId: debtorContact.id,
    });
    if ("data" in result) {
      message.success("Contact deleted successfully!");
    }
  };

  const toggleIsEditing = async () => {
    if (isEditing) {
      let values;
      try {
        values = await form.validateFields();
      } catch {
        return null; // Form will handle error messages
      }

      const action = isNew
        ? createDebtorContact({
            debtorId,
            ...values,
          })
        : updateDebtorContact({
            debtorId,
            debtorContactId: debtorContact.id,
            ...values,
          });
      const result = await action;

      if ("data" in result) {
        message.success("Saved successfully!");

        triggerReplicatePacketedDebtorUpdatesModal({
          action: replicatePacketedDebtorContacts,
          dataId: result.data.id,
          newData: `${result.data.firstName}${
            result.data.middleName ? ` ${result.data.middleName}` : ""
          } ${result.data.lastName}`,
          type: "contact",
        });

        if (isNew) onCreate();
      }

      if ("error" in result) {
        form.setFields(formatError(result.error));
        message.error("Could not update contact info");
        return;
      }
      setIsEditing(false);
    } else {
      setIsEditing(true);
      form.setFieldsValue(debtorContact);
    }
  };

  const onPrimaryCancel = () => {
    setIsEditing(false);
  };

  const formItemLayout = {
    labelCol: {
      xs: { span: 20 },
      sm: { span: 10 },
    },
    wrapperCol: {
      xs: { span: 20 },
      sm: { span: 14 },
    },
  };

  return (
    <StyledCard>
      {(isFirstItem || isNew || isEditing || !isCompactMode) && (
        <StyledHeader>
          <h4>
            <Space size="small">
              {isNew
                ? `${profileType === "consumer" ? "New Debtor Contact" : "New Contact Person"}`
                : title}
            </Space>
          </h4>
          {!isCompactMode &&
            !isNew &&
            !isEditing &&
            isAgencyUserType &&
            (isUpdateDebtorContactInfoAuthorized || isDeleteDebtorContactInfoAuthorized) && (
              <Row>
                {isUpdateDebtorContactInfoAuthorized && (
                  <Button
                    type="link"
                    icon={<EditOutlined />}
                    loading={isCreateLoading || isUpdateLoading}
                    onClick={toggleIsEditing}
                  />
                )}
                {isDeleteDebtorContactInfoAuthorized && (
                  <Popconfirm
                    placement="topRight"
                    okText="Yes"
                    title="Are you sure you want to delete this contact?"
                    onConfirm={archiveContact}
                  >
                    <Button loading={isDeleteLoading} type="link" icon={<DeleteOutlined />} />
                  </Popconfirm>
                )}
              </Row>
            )}
        </StyledHeader>
      )}
      {!isEditing && isCompactMode && (
        <StyledCompactRow>
          <StyledLeadingIcon />
          {(() => {
            const name = [debtorContact.firstName, debtorContact.middleName, debtorContact.lastName]
              .filter(Boolean)
              .join(" ");
            let relationship = null;
            if (debtorContact.relationshipToDebtor) {
              const list =
                profileType === "commercial"
                  ? constants?.commercialDebtorContactRelationships
                  : constants?.debtorContactRelationships;
              relationship =
                list?.find((item) => item.value === debtorContact.relationshipToDebtor)?.display ??
                null;
            }
            return (
              <Tooltip title={name + (relationship ? ` (${relationship})` : "")}>
                <StyledCompactPrimary>
                  {name}
                  {relationship && ` (${relationship})`}
                </StyledCompactPrimary>
              </Tooltip>
            );
          })()}
          {debtorContact.isDoNotContact && <StyledTag color="error">Do Not Contact</StyledTag>}
          {debtorContact.email && (
            <Tooltip title={debtorContact.email}>
              <StyledContactMailIcon />
            </Tooltip>
          )}
          {debtorContact.phoneNumber1 && (
            <Tooltip title={debtorContact.phoneNumber1}>
              <StyledContactPhoneIcon />
            </Tooltip>
          )}
          <StyledTrailingActions>
            <Popover
              trigger="hover"
              placement="top"
              content={
                <StyledDescriptions size="small" column={1} bordered>
                  {debtorContact.email ? (
                    <Descriptions.Item label="Email">{debtorContact.email}</Descriptions.Item>
                  ) : null}
                  {debtorContact.phoneNumber1 ? (
                    <Descriptions.Item label="Phone 1">
                      {debtorContact.phoneNumber1}
                      {debtorContact.phoneType1 &&
                        ` (${toTitleCase({ str: debtorContact.phoneType1, delimiter: "_" })})`}
                    </Descriptions.Item>
                  ) : null}
                  {debtorContact.phoneNumber2 ? (
                    <Descriptions.Item label="Phone 2">
                      {debtorContact.phoneNumber2}
                      {debtorContact.phoneType2 &&
                        ` (${toTitleCase({ str: debtorContact.phoneType2, delimiter: "_" })})`}
                    </Descriptions.Item>
                  ) : null}
                  {debtorContact.fax && profileType === "commercial" ? (
                    <Descriptions.Item label="Fax">{debtorContact.fax}</Descriptions.Item>
                  ) : null}
                  {debtorContact.preferredLanguage && profileType === "consumer" ? (
                    <Descriptions.Item label="Language">
                      {constants?.languages?.find(
                        (l) => l.value === debtorContact.preferredLanguage,
                      )?.display ?? debtorContact.preferredLanguage}
                    </Descriptions.Item>
                  ) : null}
                  {debtorContact.notes ? (
                    <Descriptions.Item label="Notes">{debtorContact.notes}</Descriptions.Item>
                  ) : null}
                  {debtorContact.dataSource ? (
                    <Descriptions.Item label="Data Source">
                      {camelToWords(snakeToCamelCase(debtorContact.dataSource))}
                    </Descriptions.Item>
                  ) : null}
                  {debtorContact.createdAt ? (
                    <Descriptions.Item label="Created At">
                      {moment(debtorContact.createdAt).format(DATE_TIME_FORMAT)}
                    </Descriptions.Item>
                  ) : null}
                </StyledDescriptions>
              }
            >
              <StyledInfoIcon />
            </Popover>
            {isAgencyUserType &&
              (isUpdateDebtorContactInfoAuthorized || isDeleteDebtorContactInfoAuthorized) && (
                <>
                  {isUpdateDebtorContactInfoAuthorized && (
                    <Button
                      type="link"
                      icon={<EditOutlined />}
                      loading={isCreateLoading || isUpdateLoading}
                      onClick={toggleIsEditing}
                    />
                  )}
                  {isDeleteDebtorContactInfoAuthorized && (
                    <Popconfirm
                      placement="topRight"
                      okText="Yes"
                      title="Are you sure you want to delete this contact?"
                      onConfirm={archiveContact}
                    >
                      <Button loading={isDeleteLoading} type="link" icon={<DeleteOutlined />} />
                    </Popconfirm>
                  )}
                </>
              )}
          </StyledTrailingActions>
        </StyledCompactRow>
      )}
      {(!isCompactMode || isEditing) && (
        <StyledForm
          labelAlign="left"
          form={form}
          {...formItemLayout}
          initialValues={{ dataSource: "" }}
        >
          {isEditing ? (
            <>
              <DisplayComponent
                isEditing={isEditing}
                slice={debtorContact}
                label="First Name"
                name="firstName"
                rules={[{ required: true }]}
              />
              <DisplayComponent
                isEditing={isEditing}
                slice={debtorContact}
                label="Middle Name"
                name="middleName"
              />
              <DisplayComponent
                isEditing={isEditing}
                slice={debtorContact}
                label="Last Name"
                name="lastName"
                rules={[{ required: true }]}
              />
            </>
          ) : (
            <StyledContainer>
              <StyledLabel>
                <span>Contact Name</span>
                {debtorContact?.notes ? (
                  <Tooltip title={debtorContact?.notes} trigger="hover">
                    <StyledInfoCircleOutlined />
                  </Tooltip>
                ) : null}
              </StyledLabel>
              <StyledValue>
                {debtorContact.firstName} {debtorContact.middleName} {debtorContact.lastName}
              </StyledValue>
            </StyledContainer>
          )}
          <DisplayComponent
            isEditing={isEditing}
            slice={debtorContact}
            label="Email"
            name="email"
          />
          {profileType === "commercial" && (
            <>
              <DisplayComponent
                isEditing={isEditing}
                slice={debtorContact}
                label="Role"
                name="relationshipToDebtor"
                rules={[{ required: true }]}
                outputFormatter={(value) =>
                  constants?.commercialDebtorContactRelationships?.find(
                    (item) => item.value === value,
                  )?.display
                }
              >
                <Select
                  showSearch
                  filterOption={(input, option) =>
                    (option.label.toString() ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  allowClear
                  popupMatchSelectWidth={false}
                  options={constants?.commercialDebtorContactRelationships?.map(
                    ({ display, value }) => ({
                      value,
                      label: display,
                    }),
                  )}
                />
              </DisplayComponent>
              <DisplayComponent
                isEditing={isEditing}
                slice={debtorContact}
                label="Fax"
                name="fax"
              />
            </>
          )}
          {profileType === "consumer" && (
            <>
              <DisplayComponent
                isEditing={isEditing}
                slice={debtorContact}
                label="Preferred Language"
                name="preferredLanguage"
                outputFormatter={(value) =>
                  constants?.languages?.find((l) => l.value === value)?.display ?? value
                }
              >
                <Select
                  showSearch
                  filterOption={(input, option) =>
                    (option.label.toString() ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  allowClear
                  popupMatchSelectWidth={false}
                  options={constants?.languages.map(({ display, value }) => ({
                    value,
                    label: display,
                  }))}
                />
              </DisplayComponent>
              <DisplayComponent
                isEditing={isEditing}
                slice={debtorContact}
                label="Relationship"
                name="relationshipToDebtor"
                rules={[{ required: true }]}
                outputFormatter={(value) =>
                  constants?.debtorContactRelationships?.find((item) => item.value === value)
                    ?.display
                }
              >
                {/* Agents shouldn't be able to create the DebtorContact as type Debtor/Self. Those
                are generated automatically on the backend and only 1 should exist. */}
                <Select
                  showSearch
                  filterOption={(input, option) =>
                    (option.label.toString() ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  allowClear
                  popupMatchSelectWidth={false}
                  options={constants?.debtorContactRelationships
                    .slice(1)
                    ?.map(({ display, value }) => ({
                      value,
                      label: display,
                    }))}
                />
              </DisplayComponent>
            </>
          )}
          {isEditing && (
            <Form.Item label="Do not contact" name="isDoNotContact" valuePropName="checked">
              <Checkbox />
            </Form.Item>
          )}
          {isEditing ? (
            <>
              <DisplayComponent
                isEditing={isEditing}
                slice={debtorContact}
                label="Phone 1"
                name="phoneNumber1"
                placeholder="XXX-XXX-XXXX"
              />
              <DisplayComponent
                isEditing={isEditing}
                slice={debtorContact}
                label="Phone 1 type"
                name="phoneType1"
              >
                <Select
                  showSearch
                  filterOption={(input, option) =>
                    (option.label.toString() ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  allowClear
                  popupMatchSelectWidth={false}
                  options={constants?.phoneTypes?.map(({ display, value }) => ({
                    value,
                    label: display,
                  }))}
                />
              </DisplayComponent>
            </>
          ) : (
            !!debtorContact.phoneNumber1 && (
              <DisplayPhoneNumber
                number={debtorContact.phoneNumber1}
                type={`(${toTitleCase({ str: debtorContact.phoneType1, delimiter: "_" })})`}
                label="Phone 1"
                isValid={debtorContact.phoneType1 !== "invalid"}
              />
            )
          )}
          {isEditing ? (
            <>
              <DisplayComponent
                isEditing={isEditing}
                slice={debtorContact}
                label="Phone 2"
                name="phoneNumber2"
                placeholder="XXX-XXX-XXXX"
              />
              <DisplayComponent
                isEditing={isEditing}
                slice={debtorContact}
                label="Phone 2 type"
                name="phoneType2"
              >
                <Select
                  showSearch
                  filterOption={(input, option) =>
                    (option.label.toString() ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  allowClear
                  popupMatchSelectWidth={false}
                  options={constants?.phoneTypes?.map(({ display, value }) => ({
                    value,
                    label: display,
                  }))}
                />
              </DisplayComponent>
            </>
          ) : (
            !!debtorContact.phoneNumber2 && (
              <DisplayPhoneNumber
                number={debtorContact.phoneNumber2}
                type={`(${toTitleCase({ str: debtorContact.phoneType2, delimiter: "_" })})`}
                label="Phone 2"
                isValid={debtorContact.phoneType2 !== "invalid"}
              />
            )
          )}
          {/* Only allow setting of data source if no data source is set, or it is editable (i.e. present in the editableDataSourceTypes) */}

          <DisplayComponent isEditing={isEditing} slice={debtorContact} label="Notes" name="notes">
            <Input.TextArea placeholder="Add Notes" />
          </DisplayComponent>
          {!isEditing && (
            <>
              <DisplayComponent
                isEditing={isEditing}
                slice={debtorContact}
                label="Data Source"
                name="dataSource"
                outputFormatter={(value) => camelToWords(snakeToCamelCase(value))}
              />
              <DisplayComponent
                isEditing={isEditing}
                slice={debtorContact}
                label="Created At"
                name="createdAt"
                outputFormatter={(value) => moment(value).format(DATE_TIME_FORMAT)}
              />
            </>
          )}
          {isEditing && (
            <>
              {(!debtorContact?.dataSource ||
                editableDataSourceTypeValues.includes(debtorContact?.dataSource)) && (
                <DisplayComponent
                  isEditing={isEditing}
                  slice={debtorContact}
                  label="Data Source"
                  name="dataSource"
                  outputFormatter={(value) => camelToWords(value)}
                >
                  <Select
                    showSearch
                    filterOption={(input, option) =>
                      (option.label.toString() ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                    popupMatchSelectWidth={false}
                    options={constants?.editableDataSourceTypes?.map(({ display, value }) => ({
                      value,
                      label: display,
                    }))}
                  />
                </DisplayComponent>
              )}
              <StyledButtonContainer>
                <Space>
                  <Button onClick={isNew ? onCancel : onPrimaryCancel}>Cancel</Button>
                  {(isAddDebtorContactInfoAuthorized || isUpdateDebtorContactInfoAuthorized) && (
                    <Button
                      loading={isCreateLoading || isUpdateLoading}
                      onClick={toggleIsEditing}
                      type="primary"
                    >
                      Save
                    </Button>
                  )}
                </Space>
              </StyledButtonContainer>
            </>
          )}
        </StyledForm>
      )}
    </StyledCard>
  );
}
